import { Injectable } from "@angular/core";
import {
  STRIPE_GATEWAY_PAYMENT_TYPES,
  PAYPAL_GATEWAY_PAYMENT_TYPES,
} from "../../interfaces/payment-integration.interface";
import { PaymentMethod } from "../../interfaces/customer-payment-method.interface";
import {
  QPilotPaymentMethod,
  QPilotPaymentMethodType,
} from "../../../../core/models/qpilot/payment-method.interface";

@Injectable({ providedIn: "root" })
export class PaymentIntegrationsHelper {
  private typeDisplayNames: Record<string, string> = {
    Stripe: "Stripe",
    AuthorizeNet: "Authorize.Net",
    PayPal: "PayPal",
    Braintree: "Braintree",
    CyberSourceV2: "CyberSource v2",
    Nmi: "NMI",
    PayaV1: "Paya v1",
    TrustCommerce: "TrustCommerce",
    Square: "Square",
    Sage: "SagePay/Opayo",
    Checkout: "Checkout.com",
    PaypalV2: "PayPal v2",
    PayPalV3: "PayPal v3",
    Airwallex: "Airwallex",
    Shopify: "Shopify",
    Other: "Other",
  };

  /**
   * Map QPilotPaymentMethodType enum to string
   */
  private typeEnumToString: Record<QPilotPaymentMethodType, string> = {
    [QPilotPaymentMethodType.Stripe]: "Stripe",
    [QPilotPaymentMethodType.AuthorizeNet]: "AuthorizeNet",
    [QPilotPaymentMethodType.Test]: "Test",
    [QPilotPaymentMethodType.PayPal]: "PayPal",
    [QPilotPaymentMethodType.PaypalV2]: "PaypalV2",
    [QPilotPaymentMethodType.Braintree]: "Braintree",
    [QPilotPaymentMethodType.Undefined]: "Other",
    [QPilotPaymentMethodType.Other]: "Other",
    [QPilotPaymentMethodType.Shopify]: "Shopify",
    [QPilotPaymentMethodType.Airwallex]: "Airwallex",
    [QPilotPaymentMethodType.Checkout]: "Checkout",
    [QPilotPaymentMethodType.CyberSourceV2]: "CyberSourceV2",
    [QPilotPaymentMethodType.Nmi]: "Nmi",
    [QPilotPaymentMethodType.PayaV1]: "PayaV1",
    [QPilotPaymentMethodType.TrustCommerce]: "TrustCommerce",
    [QPilotPaymentMethodType.Square]: "Square",
    [QPilotPaymentMethodType.Sage]: "Sage",
    [QPilotPaymentMethodType.PayPalV3]: "PayPalV3",
  };

  /**
   * Get display name for payment method type
   */
  getTypeDisplayName(paymentMethodType: string): string {
    return this.typeDisplayNames[paymentMethodType] || paymentMethodType;
  }

  /**
   * Get gateway payment type options for a given payment method type
   */
  getGatewayPaymentTypes(
    paymentMethodType: string,
  ): { code: string; name: string }[] {
    if (paymentMethodType === "Stripe") {
      return [...STRIPE_GATEWAY_PAYMENT_TYPES];
    }
    if (paymentMethodType === "PayPal") {
      return [...PAYPAL_GATEWAY_PAYMENT_TYPES];
    }
    return [];
  }

  /**
   * Check if gateway supports loading payment methods externally
   */
  supportsExternalLoad(paymentMethodType: string): boolean {
    return ["AuthorizeNet", "Stripe", "Braintree"].includes(paymentMethodType);
  }

  /**
   * Convert QPilotPaymentMethod to PaymentMethod (customers feature interface)
   * Used to bridge the core helpers with the customers feature
   */
  toPaymentMethod(qpilot: QPilotPaymentMethod): PaymentMethod {
    return {
      Id: qpilot.Id,
      CustomerId: qpilot.CustomerId,
      Type:
        qpilot.Type !== undefined
          ? this.typeEnumToString[qpilot.Type]
          : undefined,
      GatewayCustomerId: qpilot.GatewayCustomerId,
      GatewayPaymentId: qpilot.GatewayPaymentId,
      Description: qpilot.Description,
      BillingFirstName: qpilot.BillingFirstName,
      BillingLastName: qpilot.BillingLastName,
      BillingStreet1: qpilot.BillingStreet1,
      BillingStreet2: qpilot.BillingStreet2,
      BillingCity: qpilot.BillingCity,
      BillingState: qpilot.BillingState,
      BillingPostcode: qpilot.BillingPostcode,
      BillingCountry: qpilot.BillingCountry,
      Expiration: qpilot.Expiration,
      LastFourDigits: qpilot.LastFourDigits || qpilot.LastFour || qpilot.Last4,
      GatewayPaymentType:
        typeof qpilot.GatewayPaymentType === "number"
          ? qpilot.GatewayPaymentType
          : undefined,
    };
  }

  /**
   * Convert array of QPilotPaymentMethod to PaymentMethod[]
   */
  toPaymentMethods(qpilotMethods: QPilotPaymentMethod[]): PaymentMethod[] {
    return qpilotMethods.map((m) => this.toPaymentMethod(m));
  }
}
